<template>
  <div :class="wrapperClassName" v-if="visibleIfResult">
    <label :for="idInput" :class="labelClassName" v-dompurify-html="labelText"></label>

    <span class="help-trigger" title="Hilfe anzeigen" @click="toggleHelpText" v-if="help">
      <font-awesome-icon icon="question-circle" size="lg" />
    </span>
    <Message
      v-show="shouldShowHelp"
      closable
      severity="info"
      role="alert"
      class="mb-2 form-info-message"
      @close="toggleHelpText"
    >
      <span v-dompurify-html="help"></span>
    </Message>

    <span v-show="textLimitConfig.maxCount > 0" class="mx-2">
      {{ remainingCount }} / {{ textLimitConfig.maxCount }} Zeichen verfügbar
    </span>
    <textarea
      :id="idInput"
      ref="textareainput"
      v-model="value"
      :aria-invalid="hasError || hasServerError"
      :aria-describedby="idErrorText"
      :data-vv-as="labelText"
      :data-wdw-code="code"
      :name="name"
      :required="required"
      :rows="rows"
      :maxlength="textLimitConfig.maxCount === 0 ? 524288 : textLimitConfig.maxCount"
      :class="inputClassName"
      :disabled="disabled"
      tabindex="0"
      @keyup="onTextEntered($event)"
    ></textarea>
    <span :id="idErrorText" v-show="hasError" aria-atomic="true" class="text-danger form-error-message">
      {{ error }}
    </span>
    <span :id="idErrorText" v-show="hasServerError" aria-atomic="true" class="text-danger form-error-message">
      {{ serverError }}
    </span>
    <span v-show="warnOverlimit" class="text-warning">{{ textLimitConfig.cLimitText }}</span>
  </div>
</template>

<script>
import { readonly, ref, reactive, computed, watch, onMounted, toRefs } from 'vue';
import { useField } from 'vee-validate';
import useToggler from '@/composables/general/useToggler';
import { text } from '@fortawesome/fontawesome-svg-core';
import { userStore } from '@/store/user-store';
import Message from 'primevue/message';

export default {
  name: 'form-control-textarea',

  components: { Message },

  props: {
    name: {
      required: true,
      type: String,
    },
    code: {
      required: false,
      type: String,
      default: '',
    },
    label: {
      required: false,
      type: String,
      default: '',
    },
    rows: {
      type: Number,
      required: false,
      default: 5,
    },
    textLimitConfig: {
      required: false,
      type: Object,
      default() {
        return {
          maxCount: 0, // maxlength attribute will be filled with "524288" as default
          cLimitText: '',
        };
      },
    },
    additionalLabelClasses: {
      required: false,
      type: String,
      default: '',
    },
    shouldcache: {
      required: false,
      type: Boolean,
      default: false,
    },
    prefixId: {
      required: false,
      type: String,
      default: 'wdw-bms-attr',
    },
    required: {
      required: false,
      type: Boolean,
      default: false,
    },
    defaultvalue: {
      required: false,
      type: String,
      default: '',
    },
    error: {
      required: false,
      type: String,
      default: '',
    },
    serverError: {
      required: false,
      type: String,
      default: '',
    },
    help: {
      required: false,
      type: String,
      default: '',
    },
    visibleIfResult: {
      required: false,
      type: Boolean,
      default: true,
    },
    isPublic: {
      type: Boolean,
      default: true,
    },
    watchChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    reload: {
      type: Number,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  emits: ['change', 'validate'],
  setup(props, context) {
    const {
      name,
      code,
      label,
      textLimitConfig,
      required,
      defaultvalue,
      shouldcache,
      additionalLabelClasses,
      prefixId,
      error,
      serverError,
      isPublic,
      watchChange,
    } = toRefs(props);
    const { toggleValue: shouldShowHelp, toggle: toggleHelpText } = useToggler();

    const remainingCount = ref(textLimitConfig.value.maxCount);
    const warnOverlimit = ref(false);
    const textareainput = ref(null);

    //+---------------------+
    //|    Other methods    |
    //+---------------------+
    function validate(value) {
      const finalvalue = value ? value.trim() : '';
      const returnValue = {
        value: finalvalue,
        valueHuman: finalvalue,
        validated: true,
        name: name.value,
        code: code.value,
        type: 'text',
      };
      if (required.value) {
        returnValue.validated = finalvalue !== '';
      }

      if (shouldcache.value) {
        localStorage.setItem(name.value, returnValue.value);
      }

      // Emit event
      context.emit('validate', returnValue);
      return returnValue.validated ? true : error.value;
    }
    const { errorMessage, value } = useField(name, validate, {
      initialValue: defaultvalue.value,
    });

    function onTextEntered(event) {
      // Show warning on limit being reached
      const tlc = textLimitConfig.value;
      const maxCount = tlc.maxCount;
      if (maxCount && maxCount > 0) {
        remainingCount.value = maxCount - value.value.length;
        warnOverlimit.value = remainingCount.value <= 0;
      }
      // Resize the textarea
      const target = event.target;
      target.style.maxHeight = '400px';
      const cHeight = target.clientHeight;
      const sHeight = target.scrollHeight;
      if (cHeight < sHeight) {
        target.style.height = 25 + sHeight + 'px';
      }
    }

    //+---------------------+
    //|     computed()      |
    //+---------------------+
    const hasError = computed(() => {
      return errorMessage.value != undefined;
    });
    const hasServerError = computed(() => {
      return serverError.value != undefined && serverError.value !== '';
    });
    const inputClassName = computed(() => {
      return {
        'form-control': true,
        'is-invalid': hasError.value || hasServerError.value,
      };
    });
    const wrapperClassName = computed(() => {
      return {
        'form-group': true,
        'has-error': hasError.value || serverError.value,
      };
    });
    const labelClassName = computed(() => {
      return additionalLabelClasses.value + ' ' + (hasError.value || serverError.value ? 'text-danger' : '');
    });

    const labelText = computed(() => {
      return (
        label.value +
        (required.value ? '*' : '') +
        (isPublic.value ? '' : ' - <span class="text-muted">Diese Daten werden nicht veröffentlicht.</span>')
      );
    });
    const idInput = computed(() => {
      return prefixId.value + '-textarea-' + name.value;
    });
    const idErrorText = computed(() => {
      return prefixId.value + '-textarea-' + name.value + '-error';
    });

    //+----------------------------+
    //|     Lifecycle hooks        |
    //+----------------------------+
    onMounted(() => {
      const inp = textareainput.value;
      if (inp) inp.oninvalid = inp.setCustomValidity(error.value);
    });
    watch(value, (nv, ov) => {
      if (watchChange) {
        context.emit('change', value.value);
      }
    });
    watch(
      () => props.reload,
      (nv, ov) => {
        value.value = defaultvalue.value;
      }
    );
    //+---------------------+
    //|    return           |
    //+---------------------+
    return {
      // Computed
      hasError,
      hasServerError,
      wrapperClassName,
      labelClassName,
      inputClassName,
      idInput,
      idErrorText,
      labelText,
      // Listener
      onTextEntered,
      // Other methods
      toggleHelpText,
      // Variables
      shouldShowHelp,
      errorMessage,
      value,
      remainingCount,
      warnOverlimit,
    };
  },
};
</script>

<style lang="scss" scoped>
label {
  display: inline;
  font-weight: bold;
  max-width: 100%;
}
input {
  margin-top: 5px;
}
.form-group {
  margin-bottom: 15px;
}
.help-trigger {
  cursor: pointer;
  margin-left: 4px;
}
</style>
